import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import "./SkillTreePage.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STRAND_META = {
  mind: { label: "Mind", sub: "Passives", icon: "🧠" },
  body: { label: "Body", sub: "Actives", icon: "💪" },
  soul: { label: "Soul", sub: "Showtime", icon: "🔮" },
};

const TIER_LABELS = {
  1: "Tier I · Foundational Conditioning",
  2: "Tier II · Specialized Mastery",
  3: "Tier III · Advanced Optimization",
  4: "Tier IV · Transcendence · Mastery",
  5: "Tier V · Locked · Future Loop",
};

const DEFAULT_HERO = "roc";

// 🔗 Map a hero_class.class_id (Soul Key value) → skill-tree hero_id.
// Named heroes (roc, david) map to themselves. Picker classes route to
// their template tree (picker_archer, picker_warrior, etc.).
const CLASS_TO_HERO_ID = {
  archer: "picker_archer",
  warrior: "picker_warrior",
  mage: "picker_mage",
  ash_rogue: "picker_ash_rogue",
  roc: "roc",
  david: "david",
  quiet_mend: "sloane",
};

export default function SkillTreePage() {
  const { hero_id: heroIdParam } = useParams();
  const navigate = useNavigate();

  // 🎯 Active-class awareness — fetch the user's Soul-Key current class
  // so (1) we can auto-route to their tree if no URL param, and (2) we
  // can show a top-of-page breadcrumb ("You are playing as: The Archer 🏹").
  const [activeClassId, setActiveClassId] = useState(null);
  useEffect(() => {
    axios.get(`${API}/soul-key/state`)
      .then((r) => setActiveClassId(r.data?.current_class_id || null))
      .catch(() => { /* non-fatal — user may not have picked a class yet */ });
  }, []);

  const [view, setView] = useState(null);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNode, setModalNode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  // If no URL param, prefer the active-class tree over the hardcoded default,
  // BUT only if the mapped tree is authored — otherwise fall back to default
  // so we never land on an unauthored placeholder tree.
  const rawActiveHeroId = activeClassId ? (CLASS_TO_HERO_ID[activeClassId] || activeClassId) : null;
  const activeHeroIsAuthored = rawActiveHeroId
    ? (roster.find((r) => r.hero_id === rawActiveHeroId)?.authored ?? false)
    : false;
  const activeHeroId = activeHeroIsAuthored ? rawActiveHeroId : null;
  const heroId = heroIdParam || activeHeroId || DEFAULT_HERO;

  const fetchRoster = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/skill-tree/roster`);
      setRoster(r.data.roster || []);
    } catch (e) {
      /* non-fatal */
    }
  }, []);

  const fetchTree = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/skill-tree/${heroId}`);
      setView(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Skill tree failed to load: ${detail}`);
      setView(null);
    } finally {
      setLoading(false);
    }
  }, [heroId]);

  useEffect(() => { fetchRoster(); }, [fetchRoster]);
  useEffect(() => { fetchTree(); }, [fetchTree]);

  const nodesByStrand = useMemo(() => {
    const g = { mind: {}, body: {}, soul: {} };
    if (!view || !Array.isArray(view.nodes)) return g;
    for (const n of view.nodes) {
      if (!g[n.strand][n.tier]) g[n.strand][n.tier] = [];
      g[n.strand][n.tier].push(n);
    }
    return g;
  }, [view]);

  const handlePurchase = async (nodeId) => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/skill-tree/${heroId}/purchase`, { node_id: nodeId });
      setView(r.data);
      setModalNode(null);
      toast.success("Node unlocked. Growth compounds.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all Chronograph Points? (Free during this Loop.)")) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/skill-tree/${heroId}/reset`);
      setView(r.data);
      toast.success("Tree reset. Every point returned.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleSlotClick = async (nodeId) => {
    if (!view) return;
    const current = view.loadout_active_ids || [];
    let next;
    if (current.includes(nodeId)) {
      next = current.filter((id) => id !== nodeId);
    } else {
      if (current.length >= view.loadout_active_cap) {
        toast.error(`Loadout capped at ${view.loadout_active_cap} actives. Remove one first.`);
        return;
      }
      next = [...current, nodeId];
    }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/skill-tree/${heroId}/loadout`, { active_ids: next });
      setView(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Loadout update failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleSetLevel = async (level) => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/skill-tree/${heroId}/set-level`, { level });
      setView(r.data);
      toast.success(`Hero level set to ${level}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Level change failed.");
    } finally {
      setSaving(false);
    }
  };

  const pickHero = (h) => {
    setShowPicker(false);
    navigate(`/skill-tree/${h.hero_id}`);
  };

  if (loading) {
    return <div className="mbs-page"><div className="mbs-loading">Loading skill tree…</div></div>;
  }
  if (!view) {
    return (
      <div className="mbs-page">
        <div className="mbs-loading">
          <p>Tree unavailable for &quot;{heroId}&quot;.</p>
          <button className="mbs-btn primary" onClick={() => navigate(`/skill-tree/${DEFAULT_HERO}`)}>
            Back to {DEFAULT_HERO}
          </button>
        </div>
      </div>
    );
  }

  // 🌳 Canon-pending viewer — new-shape heroes (Kaelen / Mage / Warrior).
  // Read-only preview so the owner can eyeball every banked skill before
  // the engine ships. Short-circuit BEFORE any view.nodes access.
  if (view.framework === "canon_pending") {
    return (
      <CanonPreviewPage
        view={view}
        roster={roster}
        heroId={heroId}
        navigate={navigate}
        pickHero={pickHero}
        showPicker={showPicker}
        setShowPicker={setShowPicker}
      />
    );
  }

  const equippedNodes = (view.loadout_active_ids || [])
    .map((id) => view.nodes.find((n) => n.id === id))
    .filter(Boolean);

  const idColor = view.identity_color || { hex: "#fbbf24", glow: "rgba(251,191,36,0.35)" };
  const pageStyle = {
    "--hero-hex": idColor.hex,
    "--hero-glow": idColor.glow,
  };

  // 🌳 Canon-pending viewer — new-shape heroes (Kaelen / Mage / Warrior).
  // Read-only preview so the owner can eyeball every banked skill before
  // the engine ships. No CP math, no purchases, no drawer.
  if (view.framework === "canon_pending") {
    return (
      <CanonPreviewPage
        view={view}
        roster={roster}
        heroId={heroId}
        navigate={navigate}
        pickHero={pickHero}
        showPicker={showPicker}
        setShowPicker={setShowPicker}
      />
    );
  }

  return (
    <SkillTreeView
      view={view}
      roster={roster}
      heroId={heroId}
      activeHeroId={activeHeroId}
      activeClassId={activeClassId}
      navigate={navigate}
      saving={saving}
      showPicker={showPicker}
      setShowPicker={setShowPicker}
      modalNode={modalNode}
      setModalNode={setModalNode}
      pickHero={pickHero}
      handlePurchase={handlePurchase}
      handleReset={handleReset}
      handleSlotClick={handleSlotClick}
      handleSetLevel={handleSetLevel}
      idColor={idColor}
      pageStyle={pageStyle}
      equippedNodes={equippedNodes}
      nodesByStrand={nodesByStrand}
    />
  );
}

/** ─────────────────────────────────────────────────────────────
 * SkillTreeView — split out so we can use hooks for SVG line
 * measurement (refs + useLayoutEffect + ResizeObserver).
 * ───────────────────────────────────────────────────────────── */
function SkillTreeView({
  view, roster, heroId, activeHeroId, activeClassId, navigate,
  saving, showPicker, setShowPicker, modalNode, setModalNode,
  pickHero, handlePurchase, handleReset, handleSlotClick, handleSetLevel,
  idColor, pageStyle, equippedNodes, nodesByStrand,
}) {
  const treeRef = useRef(null);
  const nodeRefs = useRef(new Map()); // node_id → HTMLElement
  const [connectors, setConnectors] = useState([]);

  const recomputeConnectors = useCallback(() => {
    const container = treeRef.current;
    if (!container) return;
    const cRect = container.getBoundingClientRect();

    // Measure center of each node relative to the container
    const centers = new Map();
    for (const n of view.nodes) {
      const el = nodeRefs.current.get(n.id);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      centers.set(n.id, {
        x: r.left - cRect.left + r.width / 2,
        y: r.top - cRect.top + r.height / 2,
        node: n,
      });
    }

    // For every tier T (1..3), connect each node in T to each node in T+1
    // — a fully-connected bipartite lattice, Wartune-style.
    const lines = [];
    for (let t = 1; t <= 3; t++) {
      const upNodes = view.nodes.filter((n) => n.tier === t);
      const downNodes = view.nodes.filter((n) => n.tier === t + 1);
      for (const u of upNodes) {
        for (const d of downNodes) {
          const uc = centers.get(u.id);
          const dc = centers.get(d.id);
          if (!uc || !dc) continue;
          const upUnlocked = (u.current_level || 0) > 0;
          const downUnlocked = (d.current_level || 0) > 0;
          const forged = upUnlocked && downUnlocked;
          const live = upUnlocked && !downUnlocked;
          const sameStrand = u.strand === d.strand;
          // Line color: use the DOWNSTREAM strand color (line "leads into" that node)
          const strandColor =
            d.strand === "mind" ? "#60a5fa" :
            d.strand === "body" ? "#f87171" : "#c084fc";
          lines.push({
            key: `${u.id}→${d.id}`,
            x1: uc.x, y1: uc.y, x2: dc.x, y2: dc.y,
            color: strandColor,
            state: forged ? "forged" : live ? "live" : "dormant",
            sameStrand,
          });
        }
      }
    }
    setConnectors(lines);
  }, [view.nodes]);

  useLayoutEffect(() => { recomputeConnectors(); }, [recomputeConnectors]);
  useEffect(() => {
    const container = treeRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => recomputeConnectors());
    ro.observe(container);
    window.addEventListener("resize", recomputeConnectors);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recomputeConnectors);
    };
  }, [recomputeConnectors]);

  const attachNodeRef = (nodeId) => (el) => {
    if (el) nodeRefs.current.set(nodeId, el);
    else nodeRefs.current.delete(nodeId);
  };

  return (
    <div className="mbs-page" style={pageStyle} data-testid="skill-tree-page">
      {/* 🎯 Active-class breadcrumb — makes Soul Key swaps feel real */}
      {activeClassId && (
        <div
          className={`mbs-active-class-breadcrumb ${
            !activeHeroId
              ? "is-viewing-unauthored"
              : activeHeroId === heroId
                ? "is-viewing-active"
                : "is-viewing-other"
          }`}
          data-testid="active-class-breadcrumb"
        >
          {!activeHeroId ? (
            <>
              <span className="mbs-active-dot mbs-active-dot-muted" aria-hidden>●</span>
              Your active class <b>{activeClassId}</b> doesn&apos;t have a skill tree
              authored yet. Showing <b>{view.nameplate}</b> for now.
            </>
          ) : activeHeroId === heroId ? (
            <>
              <span className="mbs-active-dot" aria-hidden>●</span>
              You are playing as: <b>{view.nameplate}</b>
            </>
          ) : (
            <>
              <span className="mbs-active-dot mbs-active-dot-muted" aria-hidden>●</span>
              Viewing a different tree. Your active class is <b>{activeClassId}</b> —{" "}
              <button
                type="button"
                className="mbs-breadcrumb-link"
                onClick={() => navigate(`/skill-tree/${activeHeroId}`)}
                data-testid="breadcrumb-jump-active"
              >
                jump to your kit ↗
              </button>
            </>
          )}
        </div>
      )}

      {/* Header — hero nameplate + CP wallet */}
      <div className="mbs-header">
        <button
          className="mbs-class-badge mbs-nameplate-btn"
          onClick={() => setShowPicker(true)}
          data-testid="hero-picker-open"
          title="Switch hero"
        >
          <span
            className="mbs-emblem"
            style={{ filter: `drop-shadow(0 0 16px ${idColor.glow})` }}
          >
            {view.emblem}
          </span>
          <div style={{ textAlign: "left" }}>
            <h1
              className="mbs-class-title"
              style={{ color: idColor.hex, textShadow: `0 0 20px ${idColor.glow}` }}
              data-testid="hero-nameplate"
            >
              {view.nameplate}
            </h1>
            <p className="mbs-class-tagline">&ldquo;{view.tagline}&rdquo;</p>
          </div>
          <span className="mbs-nameplate-swap">▾</span>
        </button>

        <div className="mbs-wallet" data-testid="cp-wallet">
          <div>
            <div className="mbs-wallet-label">Chronograph Points</div>
            <div>
              <span
                className="mbs-wallet-value"
                style={{ color: idColor.hex }}
                data-testid="cp-available"
              >
                {view.cp_available}
              </span>
              <span className="mbs-wallet-cap"> / {view.cp_cap}</span>
            </div>
            <div className="mbs-wallet-earned">
              Lv {view.hero_level} · Earned {view.cp_earned} · Spent {view.cp_spent}
            </div>
          </div>
          <div className="mbs-actions">
            <button className="mbs-btn danger" onClick={handleReset} disabled={saving} data-testid="reset-btn">
              🔄 Free Reset
            </button>
          </div>
        </div>
      </div>

      {/* Dev testing bar — owner only */}
      <div className="mbs-header" style={{ marginTop: -10, marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: "#6b7280", letterSpacing: 0.12 }}>DEV · Simulate Hero Level:</div>
        <div className="mbs-actions">
          {[1, 15, 25, 35, 50].map((lv) => (
            <button
              key={lv}
              className={`mbs-btn ${view.hero_level === lv ? "primary" : ""}`}
              onClick={() => handleSetLevel(lv)}
              disabled={saving}
              data-testid={`set-level-${lv}`}
            >
              Lv {lv}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mbs-legend">
        <span className="mbs-legend-chip mind">🧠 MIND · Passives (Blue)</span>
        <span className="mbs-legend-chip body">💪 BODY · Actives (Red)</span>
        <span className="mbs-legend-chip soul">🔮 SOUL · Showtime (Purple)</span>
      </div>

      {/* Three-strand lattice — tiers are the rows, strands are the columns.
          Cross-strand tier junctions connect Mind ↔ Body ↔ Soul at every level. */}
      <div className="mbs-tree" data-testid="mbs-tree" ref={treeRef}>
        {/* SVG overlay: draws Wartune-style connector lines between every
            T→T+1 node pair across the whole tree. */}
        <svg className="mbs-lattice-svg" aria-hidden="true">
          {connectors.map((c) => (
            <line
              key={c.key}
              x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2}
              stroke={c.color}
              className={`mbs-lattice-line ${c.state} ${c.sameStrand ? "same-strand" : "cross-strand"}`}
            />
          ))}
        </svg>
        {/* Strand headers row */}
        <div className="mbs-strand-headers">
          {["mind", "body", "soul"].map((strand) => (
            <div key={strand} className={`mbs-strand-header-cell ${strand}`}>
              <span className="mbs-strand-icon">{STRAND_META[strand].icon}</span>
              <h2 className="mbs-strand-title">{STRAND_META[strand].label}</h2>
              <p className="mbs-strand-sub">{STRAND_META[strand].sub}</p>
            </div>
          ))}
        </div>

        {[1, 2, 3, 4].map((tier, tierIdx) => {
          const tierUnlocked =
            view.hero_level >= (view.tier_level_gates[tier] || 999) &&
            view.cp_spent >= (view.tier_cp_invested_gates?.[tier] ?? 0);
          // Aggregate "any-unlocked" per strand at this tier for junction glow
          const strandUnlockedAtTier = { mind: false, body: false, soul: false };
          ["mind", "body", "soul"].forEach((s) => {
            const arr = nodesByStrand[s][tier] || [];
            strandUnlockedAtTier[s] = arr.some((n) => n.unlocked);
          });

          // Cross-strand junction between this tier and previous
          const prevTier = tier - 1;
          const gateCp = view.tier_cp_invested_gates?.[tier] ?? 0;
          const gateLv = view.tier_level_gates?.[tier] ?? 0;
          const cpGateMet = view.cp_spent >= gateCp;
          const lvGateMet = view.hero_level >= gateLv;
          const heroSigil = view.emblem || "◆";
          const junction = tierIdx > 0 && (
            <div className={`mbs-tier-junction ${tierUnlocked ? "unlocked" : "locked"}`}
                 data-testid={`tier-junction-${tier}`}>
              {["mind", "body", "soul"].map((s) => {
                const prevArr = nodesByStrand[s][prevTier] || [];
                const curArr = nodesByStrand[s][tier] || [];
                const upUnlocked = prevArr.length > 0 && prevArr[prevArr.length - 1].unlocked;
                const downUnlocked = curArr.length > 0 && curArr[0].unlocked;
                return (
                  <div
                    key={s}
                    className={`mbs-junction-drop ${s} ${upUnlocked ? "live" : ""} ${upUnlocked && downUnlocked ? "forged" : ""} ${tierUnlocked ? "tier-unlocked" : ""}`}
                  >
                    <span
                      className="mbs-junction-sigil"
                      aria-hidden="true"
                      style={{ "--sigil-color": idColor?.hex || "currentColor" }}
                    >
                      {heroSigil}
                    </span>
                    <span className="mbs-junction-arrowhead" aria-hidden="true" />
                  </div>
                );
              })}
              <div className="mbs-junction-band" />
              {(gateCp > 0 || gateLv > 1) && (
                <span
                  className={`mbs-junction-gate ${tierUnlocked ? "met" : "unmet"}`}
                  data-testid={`tier-gate-pill-${tier}`}
                  style={tierUnlocked ? { borderColor: idColor?.hex, color: idColor?.hex, boxShadow: `0 0 14px ${idColor?.glow || 'rgba(251,191,36,0.35)'}` } : undefined}
                >
                  <span className="mbs-gate-sigil" aria-hidden="true">{heroSigil}</span>
                  {tierUnlocked ? (
                    <>Tier {tier} Open ✓</>
                  ) : !lvGateMet ? (
                    <>Lv {gateLv} required</>
                  ) : (
                    <>{gateCp} CP invested</>
                  )}
                </span>
              )}
            </div>
          );

          return (
            <React.Fragment key={tier}>
              {junction}
              <div className={`mbs-tier-label-row ${tierUnlocked ? "" : "locked"}`}>
                <span className="mbs-tier-label">
                  {TIER_LABELS[tier]}
                  {!tierUnlocked && (
                    <>
                      {view.hero_level < (view.tier_level_gates[tier] || 999) && (
                        <> · 🔒 Lv {view.tier_level_gates[tier]}</>
                      )}
                      {view.hero_level >= (view.tier_level_gates[tier] || 999) &&
                       view.cp_spent < (view.tier_cp_invested_gates?.[tier] ?? 0) && (
                        <> · 🔒 {view.tier_cp_invested_gates[tier]} CP invested</>
                      )}
                    </>
                  )}
                </span>
              </div>
              <div className="mbs-tier-row" data-testid={`tier-row-${tier}`}>
                {["mind", "body", "soul"].map((strand) => {
                  const nodes = nodesByStrand[strand][tier] || [];
                  return (
                    <div
                      key={strand}
                      className={`mbs-tier-cell ${strand} ${tierUnlocked ? "" : "locked-tier"} ${strandUnlockedAtTier[strand] ? "has-unlocked" : ""}`}
                      data-testid={`tier-cell-${strand}-${tier}`}
                    >
                      {nodes.map((n, i) => (
                        <React.Fragment key={n.id}>
                          {i > 0 && (() => {
                            const prev = nodes[i - 1];
                            const forged = prev.unlocked && n.unlocked;
                            const live = prev.unlocked && !n.unlocked;
                            return (
                              <div
                                className={`mbs-connector intra ${forged ? "forged" : ""} ${live ? "live" : ""}`}
                                data-testid={`intra-connector-${n.id}`}
                              />
                            );
                          })()}
                          <button
                            ref={attachNodeRef(n.id)}
                            className={`mbs-node ${n.current_level > 0 ? "unlocked" : ""} ${!n.tier_unlocked ? "locked-tier" : ""} ${n.is_maxed ? "maxed" : ""}`}
                            onClick={() => setModalNode(n)}
                            disabled={saving}
                            data-testid={`node-${n.id}`}
                          >
                            <span className="mbs-node-icon">{n.icon}</span>
                            <div className="mbs-node-body">
                              <div className="mbs-node-name-row">
                                <p className="mbs-node-name">{n.name}</p>
                                {(n.tags || []).includes("generator") && (
                                  <span className="mbs-tag-pill generator">⚡ GEN</span>
                                )}
                              </div>
                              <p className="mbs-node-effect">{n.effect}</p>
                              {n.effect_current && (
                                <p className="mbs-node-stat-line" data-testid={`node-stat-${n.id}`}>
                                  {n.effect_current}
                                </p>
                              )}
                              <div className="mbs-node-levels" aria-label={`Level ${n.current_level} of ${n.max_level}`}>
                                {Array.from({ length: n.max_level }).map((_, li) => (
                                  <span
                                    key={li}
                                    className={`mbs-level-dot ${li < n.current_level ? "filled" : ""}`}
                                  />
                                ))}
                                <span className="mbs-level-text">
                                  {n.current_level}/{n.max_level}
                                </span>
                              </div>
                            </div>
                            <div className="mbs-node-cost">
                              {n.is_maxed ? (
                                <>
                                  <span className="mbs-node-cost-value maxed">MAX</span>
                                  <span className="mbs-node-cost-label">Level</span>
                                </>
                              ) : (
                                <>
                                  <span className="mbs-node-cost-value">
                                    {n.next_level_cost}
                                  </span>
                                  <span className="mbs-node-cost-label">CP</span>
                                </>
                              )}
                            </div>
                          </button>
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Loadout drawer */}
      <div className="mbs-loadout" data-testid="loadout-drawer">
        <div className="mbs-loadout-inner">
          <div className="mbs-loadout-title">
            Loadout<br />
            <span style={{ fontSize: 10, opacity: 0.7 }}>
              {equippedNodes.length}/{view.loadout_active_cap} equipped
            </span>
          </div>
          <div className="mbs-loadout-slots">
            {Array.from({ length: view.loadout_active_cap }).map((_, i) => {
              const node = equippedNodes[i];
              return (
                <div
                  key={i}
                  className={`mbs-loadout-slot ${node ? `filled ${node.strand}` : ""}`}
                  title={node?.name || "Empty slot"}
                  onClick={node ? () => handleSlotClick(node.id) : undefined}
                  data-testid={`loadout-slot-${i}`}
                >
                  {node ? node.icon : "＋"}
                  {node && <span className="slot-clear">✕</span>}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: "#a5a5b8", fontStyle: "italic", maxWidth: 240 }}>
            Tap any unlocked Body or Soul node to equip. Mind (passives) always active.
          </div>
        </div>
        {view.loadout_generator_advice && (
          <div className="mbs-loadout-advice" data-testid="loadout-generator-advice">
            {view.loadout_generator_advice}
          </div>
        )}
      </div>

      {/* 🤝 Combo Ultimates — separate tier, not tied to this hero's tree.
          Unlockable when the partner hero's Soul strand is also opened. */}
      {Array.isArray(view.combos) && view.combos.length > 0 && (
        <div className="mbs-combos" data-testid="combo-ultimates-section">
          <div className="mbs-combos-header">
            <span className="mbs-combos-eyebrow">🤝 Combo Ultimates</span>
            <span className="mbs-combos-note">
              Separate tier — unlocked when both heroes&apos; Soul strands are opened.
            </span>
          </div>
          <div className="mbs-combos-grid">
            {view.combos.map((c) => (
              <div
                key={c.combo_id}
                className="mbs-combo-card"
                data-testid={`combo-card-${c.combo_id}`}
              >
                <div className="mbs-combo-head">
                  <span className="mbs-combo-icon">{c.icon}</span>
                  <div className="mbs-combo-name">{c.name}</div>
                </div>
                <div className="mbs-combo-partner">
                  <span className="mbs-combo-label">Partner</span>
                  <span className="mbs-combo-partner-name">{c.partner_nameplate}</span>
                </div>
                {(c.mana_cost != null || c.cooldown_sec != null) && (
                  <div className="mbs-combo-mechanics">
                    {c.mana_cost != null && <span className="mbs-combo-chip mana">💧 {c.mana_cost} Mana</span>}
                    {c.cooldown_sec != null && <span className="mbs-combo-chip cd">⏱ {c.cooldown_sec}s CD</span>}
                  </div>
                )}
                <p className="mbs-combo-effect">{c.effect}</p>
                {c.main_hero_note && (
                  <p className="mbs-combo-main-hero" data-testid={`combo-main-hero-${c.combo_id}`}>
                    <span className="mbs-combo-label">Theme</span>
                    <span className="mbs-combo-main-hero-body">{c.main_hero_note}</span>
                  </p>
                )}
                {c.item_flavor && (
                  <p className="mbs-combo-item-flavor" data-testid={`combo-item-flavor-${c.combo_id}`}>
                    <span className="mbs-combo-label">Item</span>
                    <span className="mbs-combo-item-body">&ldquo;{c.item_flavor}&rdquo;</span>
                  </p>
                )}
                {Array.isArray(c.mechanic) && c.mechanic.length > 0 && (
                  <ul className="mbs-combo-mechanic-list" data-testid={`combo-mechanic-${c.combo_id}`}>
                    {c.mechanic.map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </ul>
                )}
                {Array.isArray(c.requires) && c.requires.length > 0 && (
                  <ul className="mbs-combo-requires">
                    {c.requires.map((r, i) => (
                      <li key={i}>✓ {r}</li>
                    ))}
                  </ul>
                )}
                {c.cd_note && <p className="mbs-combo-cdnote">{c.cd_note}</p>}
                {c.tactical_note && (
                  <p className="mbs-combo-tactical" data-testid={`combo-tactical-${c.combo_id}`}>
                    {c.tactical_note}
                  </p>
                )}
                {c.lesson && <p className="mbs-combo-lesson">&ldquo;{c.lesson}&rdquo;</p>}
                {c.status && (
                  <div className="mbs-combo-status">{c.status}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 🎆 Catalysts Preview — locked items that grant CP or unlock nodes */}
      <div className="mbs-catalysts" data-testid="catalysts-preview-section">
        <div className="mbs-catalysts-header">
          <span className="mbs-catalysts-eyebrow">🎆 Chronograph Catalysts</span>
          <span className="mbs-catalysts-note">
            Rare consumables that permanently ignite your progression path.
          </span>
        </div>
        <div className="mbs-catalysts-grid">
          <div
            className="mbs-catalyst-card is-locked"
            data-testid="catalyst-card-chrono_core_phoenix_injector"
          >
            <div className="mbs-catalyst-locked-badge">🔒 Locked</div>
            <div className="mbs-catalyst-head">
              <span className="mbs-catalyst-icon">🎆</span>
              <div>
                <div className="mbs-catalyst-rarity">Legendary Catalyst</div>
                <div className="mbs-catalyst-name">Chrono-Core Phoenix Injector</div>
              </div>
            </div>
            <p className="mbs-catalyst-desc">
              A flame-infused catalyst designed to permanently ignite a hero&apos;s
              potential by adding raw skill points into their progression path.
            </p>
            <ul className="mbs-catalyst-mechanic">
              <li>⚡ <b>+1 Chronograph Point</b> on use (direct to your 1/60 pool)</li>
              <li>♾️ No usage limit</li>
              <li>🎆 Phoenix-wing flourish over CP counter on consume</li>
            </ul>
            <div className="mbs-catalyst-sources">
              <div className="mbs-catalyst-label">How to acquire (coming)</div>
              <ul>
                <li>🐉 Boss drop — major bosses & high-difficulty arena trials</li>
                <li>👨‍🍳 Diner Kitchen reward — David&apos;s top-tier culinary quests</li>
                <li>🗝️ Hidden chest — late-game secret areas</li>
              </ul>
            </div>
            <div className="mbs-catalyst-status">🔒 Preview only · Coming Soon</div>
          </div>
        </div>
      </div>

      {/* Hero picker modal */}
      {showPicker && (
        <div className="mbs-modal-backdrop" onClick={() => setShowPicker(false)}>
          <div
            className="mbs-modal mbs-picker-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 720 }}
          >
            <div className="mbs-modal-icon">🌳</div>
            <h3 className="mbs-modal-name">Choose your hero</h3>
            <p className="mbs-modal-strand" style={{ color: "#a5a5b8" }}>
              Each tree is unique · same layout, different soul
            </p>
            <div className="mbs-roster-grid">
              {roster.map((h) => {
                const c = h.identity_color || { hex: "#fbbf24", glow: "rgba(251,191,36,0.35)" };
                return (
                  <button
                    key={h.hero_id}
                    className={`mbs-roster-card ${h.authored ? "" : "coming-soon"} ${h.hero_id === heroId ? "active" : ""}`}
                    onClick={() => h.authored && pickHero(h)}
                    disabled={!h.authored}
                    style={{
                      borderColor: h.authored ? c.hex : "rgba(255,255,255,0.06)",
                      boxShadow: h.hero_id === heroId ? `0 0 20px -4px ${c.glow}` : "none",
                    }}
                    data-testid={`roster-card-${h.hero_id}`}
                  >
                    <span className="mbs-roster-emblem" style={{ filter: h.authored ? `drop-shadow(0 0 8px ${c.glow})` : "grayscale(1)" }}>
                      {h.emblem}
                    </span>
                    <span
                      className="mbs-roster-nameplate"
                      style={{ color: h.authored ? c.hex : "#6b7280" }}
                    >
                      {h.nameplate}
                    </span>
                    {!h.authored && <span className="mbs-roster-badge">🔒 Authoring pending</span>}
                  </button>
                );
              })}
            </div>
            <div className="mbs-modal-cta" style={{ marginTop: 20 }}>
              <button className="mbs-btn" onClick={() => setShowPicker(false)} data-testid="picker-close">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Node detail modal */}
      {modalNode && (
        <div className="mbs-modal-backdrop" onClick={() => setModalNode(null)}>
          <div className={`mbs-modal ${modalNode.strand}`} onClick={(e) => e.stopPropagation()}>
            <div className="mbs-modal-icon">{modalNode.icon}</div>
            <h3 className="mbs-modal-name">{modalNode.name}</h3>
            <p className={`mbs-modal-strand ${modalNode.strand}`}>
              {STRAND_META[modalNode.strand].label} · {TIER_LABELS[modalNode.tier]}
            </p>
            <div className="mbs-modal-levels">
              {Array.from({ length: modalNode.max_level }).map((_, li) => (
                <span key={li} className={`mbs-level-dot lg ${li < modalNode.current_level ? "filled" : ""}`} />
              ))}
              <span className="mbs-modal-level-text">
                Level {modalNode.current_level} / {modalNode.max_level}
              </span>
            </div>
            <div className="mbs-modal-effect">{modalNode.effect}</div>
            {(modalNode.effect_current || modalNode.effect_next) && (
              <div className="mbs-modal-scaling" data-testid="modal-scaling-block">
                {modalNode.effect_current && (
                  <div className="mbs-scaling-row current" data-testid="modal-effect-current">
                    <span className="mbs-scaling-label">Rank {modalNode.current_level}</span>
                    <span className="mbs-scaling-value">{modalNode.effect_current}</span>
                  </div>
                )}
                {modalNode.effect_next && !modalNode.is_maxed && (
                  <div className="mbs-scaling-row next" data-testid="modal-effect-next">
                    <span className="mbs-scaling-label">→ Rank {modalNode.current_level + 1}</span>
                    <span className="mbs-scaling-value">{modalNode.effect_next}</span>
                  </div>
                )}
                {Array.isArray(modalNode.scaling_table) && modalNode.scaling_table.length > 0 && (
                  <details open className="mbs-scaling-table-details" data-testid="modal-scaling-table">
                    <summary>Full scaling table ({modalNode.scaling_table.length} ranks)</summary>
                    <table className="mbs-scaling-table">
                      <tbody>
                        {modalNode.scaling_table.map((row, idx) => {
                          // Compute Δ vs previous rank by extracting the first numeric value.
                          const numMatch = String(row.effect_line || "").match(/-?\d+(?:\.\d+)?/);
                          const cur = numMatch ? Number(numMatch[0]) : null;
                          const prevRow = idx > 0 ? modalNode.scaling_table[idx - 1] : null;
                          const prevMatch = prevRow ? String(prevRow.effect_line || "").match(/-?\d+(?:\.\d+)?/) : null;
                          const prev = prevMatch ? Number(prevMatch[0]) : null;
                          const delta = (cur != null && prev != null) ? cur - prev : null;
                          return (
                            <tr
                              key={row.rank}
                              className={row.rank === modalNode.current_level ? "current" : ""}
                            >
                              <td className="rank">Lv {row.rank}</td>
                              <td className="effect">{row.effect_line}</td>
                              <td className="delta">
                                {delta != null && delta > 0 ? `+${delta}` : idx === 0 ? "—" : ""}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </details>
                )}
              </div>
            )}
            <div
              className="mbs-modal-lesson"
              style={{
                color: modalNode.strand === "mind" ? "#60a5fa"
                  : modalNode.strand === "body" ? "#f87171" : "#c084fc",
              }}
            >
              &ldquo;{modalNode.lesson}&rdquo;
            </div>
            <div className="mbs-modal-cta">
              <button className="mbs-btn" onClick={() => setModalNode(null)} data-testid="modal-cancel">Close</button>
              {!modalNode.is_maxed && (
                <button
                  className="mbs-btn primary"
                  onClick={() => handlePurchase(modalNode.id)}
                  disabled={saving || !modalNode.tier_unlocked || !modalNode.affordable}
                  style={{ background: idColor.hex, borderColor: idColor.hex }}
                  data-testid="modal-purchase"
                >
                  {!modalNode.tier_level_met
                    ? `🔒 Reach Lv ${view.tier_level_gates[modalNode.tier]}`
                    : !modalNode.tier_cp_gate_met
                    ? `🔒 Invest ${modalNode.tier_cp_gate_required} CP first`
                    : !modalNode.intra_tier_met
                    ? `🔒 ${modalNode.intra_tier_reason}`
                    : !modalNode.affordable
                    ? `Need ${modalNode.next_level_cost} CP`
                    : modalNode.current_level === 0
                    ? `Unlock — ${modalNode.next_level_cost} CP`
                    : `Upgrade to Lv ${modalNode.current_level + 1} — ${modalNode.next_level_cost} CP`}
                </button>
              )}
              {modalNode.current_level > 0 && (modalNode.kind === "active" || modalNode.kind === "showtime") && (
                <button
                  className="mbs-btn"
                  onClick={() => { handleSlotClick(modalNode.id); setModalNode(null); }}
                  disabled={saving}
                  data-testid="modal-toggle-equip"
                >
                  {view.loadout_active_ids.includes(modalNode.id) ? "Unequip" : "Equip"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────
 * 🌳 CanonPreviewPage — read-only viewer for `framework: canon_pending`
 * heroes (Kaelen / Mage / Warrior as of Mar 5, 2026). No CP math, no
 * purchases, no drawer — just the banked canon so the owner can
 * eyeball every skill before the engine ships.
 * ────────────────────────────────────────────────────────────── */
const BRANCH_META = {
  body:       { label: "Body",       tint: "#ef4444", glyph: "🩸" },
  mind:       { label: "Mind",       tint: "#eab308", glyph: "🧠" },
  soul:       { label: "Soul",       tint: "#a78bfa", glyph: "✨" },
  combo:      { label: "Combo",      tint: "#22d3ee", glyph: "🤝" },
  unassigned: { label: "Unassigned", tint: "#71717a", glyph: "❔" },
};

const ROLE_LABEL = {
  generator:      "⚡ Generator",
  secondary:      "★ Secondary",
  active:         "Active",
  defensive:      "🛡 Defensive",
  passive:        "Passive",
  super_passive:  "Super Passive",
  ultimate:       "🟠 Ultimate",
  super_ultimate: "🟣 Super Ultimate",
  combo:          "🤝 Combo",
};

function CanonPreviewPage({ view, roster, heroId, navigate, pickHero, showPicker, setShowPicker }) {
  const idc = view.identity_color || { hex: "#fbbf24", glow: "rgba(251,191,36,0.35)" };
  const startingSet = new Set(view.starting_reveal || []);
  const branchOrder = ["body", "mind", "soul", "combo", "unassigned"];
  return (
    <div
      className="min-h-screen bg-neutral-950 text-neutral-100 pb-24"
      data-testid={`canon-preview-${heroId}`}
      style={{ "--hero-hex": idc.hex, "--hero-glow": idc.glow }}
    >
      <div
        className="border-b border-neutral-800 px-4 py-4 sticky top-0 z-10 bg-neutral-950/95 backdrop-blur"
        style={{ borderColor: idc.hex + "44" }}
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em]" style={{ color: idc.hex }}>
              Canon Preview · Engine Pending
            </div>
            <h1 className="text-2xl md:text-3xl font-black" style={{ fontFamily: "Anton, sans-serif" }}>
              {view.nameplate}
            </h1>
            {view.tagline && (
              <p className="text-xs text-neutral-400 italic mt-0.5 max-w-2xl">{view.tagline}</p>
            )}
          </div>
          <button
            onClick={() => setShowPicker(!showPicker)}
            data-testid="canon-preview-hero-picker"
            className="px-3 py-1.5 rounded-md border border-neutral-700 hover:border-neutral-500 text-xs font-black uppercase tracking-widest"
          >
            Switch Hero
          </button>
        </div>
        <div className="max-w-5xl mx-auto mt-2 flex flex-wrap gap-2 text-[11px]">
          {view.chronograph_points != null && (
            <span className="px-2 py-0.5 rounded-full border" style={{ borderColor: idc.hex + "55", color: idc.hex }}>
              {view.chronograph_points} Chronograph Points
            </span>
          )}
          <span className="px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-300">
            {view.total_nodes} nodes
          </span>
          {view.authoring_status && (
            <span className="px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-400 italic">
              {view.authoring_status}
            </span>
          )}
        </div>
      </div>

      {showPicker && (
        <div className="max-w-5xl mx-auto px-4 mt-3">
          <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-2 flex flex-wrap gap-1.5">
            {(roster || []).filter((r) => r.authored).map((r) => (
              <button
                key={r.hero_id}
                onClick={() => { pickHero(r.hero_id); setShowPicker(false); }}
                className={`px-2.5 py-1.5 rounded-md text-xs border ${
                  r.hero_id === heroId
                    ? "border-yellow-500 bg-yellow-500/10 text-yellow-200"
                    : "border-neutral-700 hover:border-neutral-500 text-neutral-300"
                }`}
                data-testid={`canon-preview-pick-${r.hero_id}`}
              >
                {r.nameplate}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 mt-5 space-y-6">
        {view.description && (
          <p className="text-sm text-neutral-300 leading-relaxed border-l-2 pl-3" style={{ borderColor: idc.hex + "88" }}>
            {view.description}
          </p>
        )}

        {(view.stack_cap_exceptions || []).length > 0 && (
          <div className="rounded-md border border-amber-700/50 bg-amber-950/20 p-3 text-xs">
            <div className="text-amber-300 font-black uppercase tracking-widest mb-1">Stack-Cap Exceptions</div>
            {view.stack_cap_exceptions.map((s, i) => (
              <div key={i} className="text-amber-100/80">• {s}</div>
            ))}
          </div>
        )}

        {(view.starting_reveal || []).length > 0 && (
          <div className="text-xs text-neutral-400">
            <span className="font-black uppercase tracking-widest text-neutral-300">Starting Reveal (Player mode):</span>{" "}
            {view.starting_reveal.join(" · ")}
          </div>
        )}

        {branchOrder.map((b) => {
          const nodes = (view.branches || {})[b] || [];
          if (!nodes.length) return null;
          const meta = BRANCH_META[b];
          return (
            <section key={b} className="space-y-2" data-testid={`canon-preview-branch-${b}`}>
              <h2
                className="text-lg font-black uppercase tracking-widest flex items-center gap-2"
                style={{ color: meta.tint }}
              >
                <span>{meta.glyph}</span>
                <span>{meta.label}</span>
                <span className="text-xs text-neutral-500 font-normal normal-case tracking-normal">
                  {nodes.length} node{nodes.length === 1 ? "" : "s"}
                </span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {nodes.map((n) => (
                  <div
                    key={n.preview_id}
                    className="rounded-lg border bg-neutral-900/60 p-3 relative"
                    style={{ borderColor: meta.tint + "55" }}
                    data-testid={`canon-preview-node-${n.preview_id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-lg shrink-0">{view.emblem}</span>
                        <span className="font-black text-sm truncate" style={{ color: meta.tint }}>
                          {n.name}
                        </span>
                        {startingSet.has(n.name) && (
                          <span className="shrink-0 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-900/50 border border-emerald-700 text-emerald-200">
                            Start 1/5
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {n.tier != null && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-800 border border-neutral-700 text-neutral-300">
                            {n.tier === "combo" ? "Combo" : `T${romanize(n.tier)}`}
                          </span>
                        )}
                        {n.mana != null && n.mana !== 0 && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-950/60 border border-blue-800 text-blue-200">
                            {n.mana} MP
                          </span>
                        )}
                        {n.mana === 0 && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-800 border border-neutral-700 text-neutral-400">
                            0 MP
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">
                      {ROLE_LABEL[n.role] || n.role}
                      {n.partner && <span className="text-cyan-300 normal-case ml-1">· with {n.partner}</span>}
                    </div>
                    <p className="text-[12.5px] text-neutral-200 leading-relaxed whitespace-pre-line">
                      {n.effect}
                    </p>
                    {n.stack_cap_exception && (
                      <div className="mt-1.5 text-[10px] text-amber-300 italic">
                        ⚠ Stack-cap exception applies to this node.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        <div className="text-[10px] text-neutral-600 italic pt-4 border-t border-neutral-800">
          {view.preview_note}
        </div>
      </div>
    </div>
  );
}

function romanize(n) {
  if (n === 1) return "I";
  if (n === 2) return "II";
  if (n === 3) return "III";
  if (n === 4) return "IV";
  return String(n);
}

